import { FC } from 'react'

import { Box, DataSeries, colors } from '@factorialco/deprecated-design-system'
import { CartesianMarkerProps } from '@nivo/core'
import { SymbolProps } from '@nivo/legends/dist/types/svg/symbols/types'
import { Layer, ResponsiveLine } from '@nivo/line'
import { PerformanceGoalsMetricHistoryChartFragment } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { NormalizedQuantitativeMetric } from 'modules/goals/lib/normalizeMetric'

interface Props {
  metric: NormalizedQuantitativeMetric<PerformanceGoalsMetricHistoryChartFragment>
}

type DataPoints = DataSeries<Date>['data']

const reduceDataPoints = ({ dataPoints }: { dataPoints: DataPoints }): DataPoints => {
  const groupDataByDays: Record<string, number> = dataPoints
    .sort((a, b) => a.x.getTime() - b.x.getTime())
    .reduce((acc: Record<string, number>, point) => {
      const date = moment(point.x).format('YYYY-DD-MM')
      return { ...acc, [date]: point.y }
    }, {})

  return Object.keys(groupDataByDays).map((date) => ({
    x: moment(date, 'YYYY-DD-MM').toDate(),
    y: groupDataByDays[date] || 0,
  }))
}

// https://nivo.rocks/storybook/?path=/story/line--custom-line-style
const DashedSolidLine: Layer = ({ series, lineGenerator, xScale, yScale }) => {
  return series.map(({ id, data, color }, index) => (
    <path
      key={id}
      d={
        lineGenerator(
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          data.map((d) => ({
            x: xScale(Number(d.data.x)),
            y: yScale(Number(d.data.y)),
          })) as unknown as any
        ) || undefined
      }
      fill="none"
      stroke={color}
      style={
        index % 2 === 0
          ? {
              strokeDasharray: '3, 6',
              strokeWidth: 2,
            }
          : {
              strokeWidth: 2,
            }
      }
    />
  ))
}

const CustomSymbolShape: FC<SymbolProps> = ({ x, y, size, fill, borderWidth, borderColor, id }) => {
  return (
    <line
      x1={x}
      y1={y + size / 2}
      x2={x + size}
      y2={y + size / 2}
      stroke={fill}
      strokeWidth={2}
      strokeDasharray={fill !== colors.teal1000 ? '2 4' : undefined}
      style={{ pointerEvents: 'none' }}
    />
  )
}

export const MetricHistoryChart: React.FC<Props> = ({ metric }) => {
  const startValue = metric.quantitativeValue?.startValue || 0
  const targetValue = metric.quantitativeValue?.targetValue || 0
  const startOn = moment(metric.startOn).toDate()
  const endOn = moment(metric.endOn).toDate()

  const currentValue = metric.history.reduce(
    (acc, point) => {
      const date = moment(point.createdAt).toDate()
      return date > acc.date ? { date, value: point.quantitativeValue?.currentValue || 0 } : acc
    },
    { date: startOn, value: startValue }
  ).value

  const today = moment().startOf('day').toDate()

  const dataPoints = reduceDataPoints({
    dataPoints: [
      { x: startOn, y: startValue },
      ...metric.history.map((point) => ({
        x: moment(point.createdAt).toDate(),
        y: point.quantitativeValue?.currentValue || 0,
      })),
      ...(today > endOn ? [{ x: endOn, y: targetValue }] : [{ x: today, y: currentValue }]),
    ],
  })

  const maxValue = Math.max(startValue, targetValue, ...dataPoints.map((point) => point.y))
  const minValue = Math.min(startValue, targetValue, ...dataPoints.map((point) => point.y))

  const data: DataSeries<Date>[] = [
    {
      id: i18n.t('performance_goals.metric_history_chart.expected'),
      data: [
        {
          x: startOn,
          y: startValue,
        },
        {
          x: endOn,
          y: targetValue,
        },
      ],
    },
    {
      id: i18n.t('performance_goals.metric_history_chart.value'),
      data: dataPoints,
    },
  ]

  let markers: CartesianMarkerProps[] = []

  if (today < endOn && today > startOn) {
    markers = [
      ...markers,
      {
        axis: 'x',
        legend: i18n.t('performance_goals.metric_history_chart.today'),
        legendPosition: 'top',
        lineStyle: {
          strokeWidth: 1,
          stroke: colors.grey600,
        },
        value: today,
      },
    ]
  }

  markers = [
    ...markers,
    ...(((startValue < targetValue && targetValue < maxValue) ||
    (startValue > targetValue && targetValue > maxValue) ||
    startValue === targetValue
      ? [
          {
            axis: 'y',
            value: targetValue,
            legend: i18n.t('performance_goals.metric_history_chart.target'),
            legendPosition: targetValue > startValue ? 'bottom' : 'top',
            lineStyle: {
              strokeWidth: 1,
              stroke: colors.grey600,
            },
          },
        ]
      : []) satisfies CartesianMarkerProps[]),
  ] satisfies CartesianMarkerProps[]

  return (
    <Box minHeight="s300" height="s300">
      <ResponsiveLine
        data={data}
        curve="monotoneX"
        xScale={{
          precision: 'day',
          type: 'time',
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 14,
          tickValues: [
            startOn,
            ...(Math.abs(moment(today).diff(endOn, 'days')) >
            Math.abs(moment(startOn).diff(endOn, 'days')) / 8
              ? [today]
              : []),
            endOn,
          ],
          format: (value) => moment(value).format('L'),
        }}
        yScale={{
          type: 'linear',
          nice: true,
          max: maxValue,
          min: minValue,
        }}
        yFormat={(value) => i18n.formatNumber(Number(value))}
        axisLeft={{
          tickSize: 0,
          tickValues: 5,
        }}
        gridYValues={5}
        colors={[colors.grey600, colors.teal1000]}
        pointColor={[colors.champagne1000]}
        markers={markers}
        enableSlices="x"
        lineWidth={3}
        useMesh
        enableGridX={false}
        layers={[
          'grid',
          'markers',
          'axes',
          'areas',
          'crosshair',
          'slices',
          'points',
          'mesh',
          'legends',
          DashedSolidLine,
        ]}
        theme={{
          grid: {
            line: {
              stroke: '#E2E2E5',
              strokeWidth: 1,
            },
          },
        }}
        legends={[
          {
            direction: 'row',
            symbolShape: CustomSymbolShape,
            itemDirection: 'left-to-right',
            itemHeight: 20,
            itemWidth: 100,
            anchor: 'top-left',
            translateY: -60,
            translateX: -20,
          },
        ]}
        margin={{ left: 50, top: 60, right: 40, bottom: 30 }}
      />
    </Box>
  )
}
